import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { backendPost } from "../../services/dataService.js";
import Button from "../../theme/components/Button.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "../MDDForm/components/ContentDialog.jsx";
import ClaimCostsEditor from "../claim/costs/ClaimCostsEditor.jsx";

const ClaimCostsModal = ({ claimId, costs = [], open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [editingCosts, setEditingCosts] = useState(costs);
  const [isSaving, setIsSaving] = useState(false);

  // Reset editing costs when modal opens with new costs
  useEffect(() => {
    if (open) {
      setEditingCosts(costs);
    }
  }, [open, costs]);

  // ContentDialog state
  const cdState = ContentDialogInit();
  cdState.openCD = open;
  cdState.setOpenCD = (value) => {
    if (!value) {
      onClose();
    }
  };

  const handleCostsChange = (updatedCosts) => {
    setEditingCosts(updatedCosts);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Use sync endpoint - backend handles create/update/delete logic
      // and protects locked costs from modification
      await backendPost(`data/schadens/${claimId}/costs/sync`, {
        label: "Kostenpositionen speichern",
        toSend: { costs: editingCosts },
        enqueueSnackbar,
      });

      enqueueSnackbar("Kostenpositionen erfolgreich gespeichert.", {
        variant: "success",
      });

      // Notify parent to refresh data
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Error saving costs:", error);
      enqueueSnackbar("Fehler beim Speichern der Kostenpositionen.", {
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Define tca (title, content, actions) for ContentDialog
  cdState.tca = {
    title: "Kostenpositionen bearbeiten",
    content: (
      <ClaimCostsEditor
        value={editingCosts}
        onChange={handleCostsChange}
        disabled={isSaving}
      />
    ),
    actions: (
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button variant="tertiary" onClick={handleClose} disabled={isSaving}>
          Abbrechen
        </Button>
        <Button onClick={handleSave} disabled={isSaving} loading={isSaving}>
          Speichern
        </Button>
      </Box>
    ),
  };

  return (
    <ContentDialog
      openCD={open}
      setOpenCD={(value) => {
        if (!value) {
          onClose();
        }
      }}
      tca={cdState.tca}
      maxWidth="md"
      onClose={handleClose}
      disableBackdropClick={true}
    />
  );
};

ClaimCostsModal.propTypes = {
  claimId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  costs: PropTypes.array,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default ClaimCostsModal;
